import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { rawApi } from '../api/client';
import { ApiError } from '../api/errors';
import { listKey } from '../api/queryKeys';
import { useLocationId } from '../hooks/useSeedLocation';
import { notifyDone, notifyError } from '../lib/notify';
import { FloorMap, type Spot } from './floor/FloorMap';
import { type SpotDraftPosition, SpotPropertiesModal } from './floor/SpotPropertiesModal';
import { isAssignableKind, kindLabel, mirrorable, newSpotOfKind, PALETTE } from './floor/shapes';

interface Zone {
  id: string;
  name: string;
  nameEs?: string | null;
  displayOrder?: number;
  active?: boolean;
  /** The room's own outline in plan units. Null means it still grows to fit its contents. */
  width?: number | null;
  height?: number | null;
}
interface ZoneWithSpots {
  zone: Zone;
  spots: Spot[];
}

const FLOOR_PATH = '/admin/v1/floor/zones';

/**
 * What to call one thing in a sentence about it.
 *
 * Scenery is deliberately left unnamed — nobody types "Palm 3" — so a message built from labels
 * alone reads "  and   are gone from the plan". What it IS is the name it has.
 */
function spotName(spot: Spot): string {
  return spot.label.trim() || kindLabel(spot.kind);
}

/**
 * The plurals that "+s" gets wrong. Everything else on the palette takes it: tables, walls, planters.
 *
 * Here because lassoing five palms and removing them in one go is the whole reason for picking more
 * than one thing, and the message for that read "palm or tree and palm or tree".
 */
const PLURAL_OF: Record<string, string> = {
  'palm or tree': 'palms and trees',
  'sign or marker': 'signs and markers',
  steps: 'sets of steps',
};

/**
 * Naming what was actually touched, up to a point.
 *
 * "3 things removed" is a number the owner has to take on trust; "Beach 3, Beach 4 and 2 more" is
 * something he can check against the plan in front of him. Two names is where the sentence stops
 * being readable, so the rest becomes a count — and things sharing a name are counted rather than
 * repeated, because scenery carries no name and five of them all answer to "planter".
 */
function nameList(names: string[]): string {
  const parts = [...new Set(names)].map((name) => {
    const same = names.filter((n) => n === name).length;
    return same === 1 ? name : `${same} ${PLURAL_OF[name] ?? `${name}s`}`;
  });
  if (parts.length === 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} and ${parts.length - 2} more`;
}

/**
 * What can be put on the plan.
 *
 * The seeded plan had a sea wall and palm trees on it and no screen offered a way to add either —
 * double-clicking the floor always made a table, so the only route to a palm was to add a table and
 * then change what it was. A designer looking at their own bar could not tell whether the room's
 * furniture was theirs to place at all.
 */
function Palette({ placing, onPick }: { placing: string | null; onPick: (kind: string | null) => void }) {
  return (
    <Paper withBorder p="sm" radius="md" mb="md">
      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text size="sm" fw={500}>
          Add to the plan
        </Text>
        {placing && (
          <Button size="compact-xs" variant="subtle" onClick={() => onPick(null)}>
            Cancel
          </Button>
        )}
      </Group>
      <Stack gap="xs">
        {PALETTE.map((group) => (
          <div key={group.group}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }} mb={4}>
              {group.group}
            </Text>
            <Group gap="xs" wrap="wrap">
              {group.items.map((item) => (
                <Button
                  key={item.kind}
                  size="compact-sm"
                  variant={placing === item.kind ? 'filled' : 'default'}
                  onClick={() => onPick(placing === item.kind ? null : item.kind)}
                >
                  {item.label}
                </Button>
              ))}
            </Group>
          </div>
        ))}
      </Stack>
    </Paper>
  );
}

/** Row geometry for the order list. Fixed, so the row under the pointer is arithmetic, not a search. */
const ORDER_ROW_HEIGHT = 38;
const ORDER_ROW_GAP = 6;

/** Moves one id to a position, leaving the rest in the order they were. */
function movedTo(ids: string[], id: string, to: number): string[] {
  const from = ids.indexOf(id);
  if (from < 0 || to < 0 || to >= ids.length || to === from) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/**
 * The order the areas come in, saved whole.
 *
 * Sent as one list rather than a number per area, because a run of separate saves can be
 * interrupted halfway and leave two areas holding the same position — the exact fault the owner
 * reported. While a save is in flight another move is queued rather than raced, so the order he
 * finally left the list in is the one that lands, whichever request happens to come back first.
 */
function useAreaOrder(zones: ZoneWithSpots[] | undefined) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string[] | null>(null);
  const inFlight = useRef(false);
  const queued = useRef<string[] | null>(null);

  const save = useMutation({
    mutationFn: (zoneIds: string[]) => rawApi('PATCH', '/admin/v1/floor/zones/order', { zoneIds }),
    onSuccess: async () => {
      const next = queued.current;
      queued.current = null;
      if (next) {
        save.mutate(next);
        return;
      }
      inFlight.current = false;
      await queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });
      setPending(null);
    },
    onError: (err) => {
      queued.current = null;
      inFlight.current = false;
      // Snap back to what is actually on file. A list still showing an order that never saved is
      // how the owner ends up dragging the same area into place twice and trusting neither.
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });
      notifyError(err, 'save the order of the areas');
    },
  });

  const onFile = (zones ?? []).map((z) => z.zone.id);
  // An area added or removed elsewhere while a save was in flight makes the local order a lie
  // about a list that no longer exists, so it is dropped rather than drawn.
  const stale =
    pending !== null && (pending.length !== onFile.length || !pending.every((id) => onFile.includes(id)));
  const order = pending && !stale ? pending : onFile;

  const reorder = (ids: string[]) => {
    setPending(ids);
    if (inFlight.current) {
      queued.current = ids;
      return;
    }
    inFlight.current = true;
    save.mutate(ids);
  };

  return { order, reorder };
}

/**
 * The order the areas are read in, dragged rather than typed.
 *
 * This used to be a box you typed a number into, and the owner named both ways it went wrong: two
 * areas could carry the same number, and the numbers could run 0, 4, 40 with nothing in between.
 * Neither can be said here — the list IS the order, and saving it hands the positions out.
 *
 * The up and down buttons are not a fallback nobody uses. A drag-only reorder cannot be done at all
 * without a mouse, and moving one area one place is most reorders — a click beats a drag for that
 * even with a trackpad under your hand.
 */
function AreaOrder({
  areas,
  order,
  onReorder,
}: {
  areas: Zone[];
  order: string[];
  onReorder: (ids: string[]) => void;
}) {
  const [dragging, setDragging] = useState<{ id: string; ids: string[] } | null>(null);
  // The same drag, kept where a pointer event can read it without waiting for a render. A quick
  // flick can deliver the whole press-move-release before React has re-rendered once, and reading
  // the drag from state alone drops it on the floor — the area springs back and nothing saves.
  const drag = useRef<{ id: string; ids: string[] } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const ids = dragging?.ids ?? order;
  const byId = new Map(areas.map((a) => [a.id, a]));

  const holdAt = (held: { id: string; ids: string[] } | null) => {
    drag.current = held;
    setDragging(held);
  };

  return (
    <Paper withBorder p="sm" radius="md" mb="md">
      <Text size="sm" fw={500}>
        Order of the areas
      </Text>
      <Text size="xs" c="dimmed" mb="xs">
        Drag an area up or down. This is the order they appear in here and on the register.
      </Text>
      {/* The move and the release are watched by the list, not by each row: an area dragged past
          its neighbor is no longer under the row that was grabbed, and hanging the handlers off
          that one row makes the drag depend on pointer capture holding for its whole length. */}
      <div
        ref={listRef}
        onPointerMove={(e) => {
          const held = drag.current;
          if (!held) return;
          const box = listRef.current?.getBoundingClientRect();
          if (!box) return;
          const step = ORDER_ROW_HEIGHT + ORDER_ROW_GAP;
          const to = Math.max(0, Math.min(held.ids.length - 1, Math.floor((e.clientY - box.top) / step)));
          const next = movedTo(held.ids, held.id, to);
          if (next !== held.ids) holdAt({ id: held.id, ids: next });
        }}
        onPointerUp={() => {
          const held = drag.current;
          if (!held) return;
          holdAt(null);
          if (held.ids.join() !== order.join()) onReorder(held.ids);
        }}
        onPointerCancel={() => holdAt(null)}
        style={{ display: 'flex', flexDirection: 'column', gap: ORDER_ROW_GAP }}
      >
        {ids.map((id, index) => {
          const area = byId.get(id);
          if (!area) return null;
          const held = dragging?.id === id;
          return (
            // The row is the grab area and carries no action of its own — the two buttons beside
            // it are the whole of what it can do, and they are reachable by keyboard.
            <div
              key={id}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                // Capture keeps the drag alive when the pointer wanders off the list; a browser
                // that refuses it still gets a working drag from the handlers on the list itself.
                try {
                  listRef.current?.setPointerCapture(e.pointerId);
                } catch {
                  // Nothing to do: the list is still watching move and release either way.
                }
                holdAt({ id, ids: order });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: ORDER_ROW_HEIGHT,
                padding: '0 8px',
                borderRadius: 6,
                border: '1px solid var(--mantine-color-default-border)',
                background: held ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-body)',
                cursor: 'grab',
                touchAction: 'none',
                // Without this, dragging an area drags a blue text selection across the names with
                // it, and the list reads as though something has gone wrong.
                userSelect: 'none',
              }}
            >
              <Text span c="dimmed" aria-hidden>
                ⠿
              </Text>
              <Text size="sm" style={{ flex: 1 }}>
                {area.name}
              </Text>
              {area.active === false && (
                <Badge size="xs" color="gray" variant="light">
                  Not in use
                </Badge>
              )}
              <Group gap={4} wrap="nowrap" onPointerDown={(e) => e.stopPropagation()}>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  disabled={index === 0}
                  aria-label={`Move ${area.name} up`}
                  onClick={() => onReorder(movedTo(order, id, index - 1))}
                >
                  Up
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  disabled={index === ids.length - 1}
                  aria-label={`Move ${area.name} down`}
                  onClick={() => onReorder(movedTo(order, id, index + 1))}
                >
                  Down
                </Button>
              </Group>
            </div>
          );
        })}
      </div>
    </Paper>
  );
}

/**
 * The floor plan, edited on the floor plan.
 *
 * This page used to be a map with two CRUD tables underneath listing the same seating — one for
 * areas, one for every table and chair. Reading a table of your own furniture is a strange way to
 * answer a question you can see the answer to: the map already shows what is where, and the thing
 * you actually want is to change *that* table, the one you are pointing at. So the tables are gone.
 * Pick something from the palette and click the floor to put it there, click to pick something out,
 * drag to rearrange, and Remove or the Delete key to take it off.
 */
export function FloorPage() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  /** Everything picked on the plan. A list, because a row of stools gets moved and removed as one. */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editing, setEditing] = useState<Spot | null>(null);
  const [adding, setAdding] = useState<SpotDraftPosition | null>(null);
  /** What the next click on the floor will place. Null means the plan is only being rearranged. */
  const [placing, setPlacing] = useState<string | null>(null);
  /** Room sizes being dragged, staged like a move so one Save layout covers the whole arrangement. */
  const [roomSize, setRoomSize] = useState<Record<string, { width: number; height: number }>>({});
  /** What each area's furniture reaches, reported by the map, so the size can be offered not guessed. */
  const [extents, setExtents] = useState<
    Record<string, { contentW: number; contentH: number; outside: number }>
  >({});

  const saveRoom = useMutation({
    mutationFn: ({ id, width, height }: { id: string; width: number; height: number }) =>
      rawApi('PATCH', `${FLOOR_PATH}/${id}`, { width, height }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });
      setRoomSize({});
    },
    onError: (err) => notifyError(err, 'save the size of this area'),
  });

  /**
   * Scenery goes straight down. A palm tree has no name worth typing and no seats to declare, so a
   * dialog between the click and the tree is pure friction — and laying a sea wall means placing
   * six of them in a row. Somewhere people SIT still opens, because staff say its name out loud.
   */
  const dropDecor = useMutation({
    mutationFn: (at: SpotDraftPosition) =>
      rawApi('POST', '/admin/v1/floor/spots', {
        ...newSpotOfKind(String(at.kind)),
        zoneId: at.zoneId,
        x: at.x,
        y: at.y,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) }),
    onError: (err) => notifyError(err, 'add that to the plan'),
  });
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [addingArea, setAddingArea] = useState(false);
  /** Drags are held locally until saved, so rearranging a room is one request, not one per pixel. */
  // A stretch stages exactly like a move, so undoing or saving the arrangement covers both. A wall
  // dragged to length and a table nudged left are one edit to the room, not two kinds of edit.
  const [moved, setMoved] = useState<
    Record<string, { x?: number; y?: number; width?: number; height?: number }>
  >({});

  const zones = useQuery({
    queryKey: listKey(FLOOR_PATH),
    queryFn: () => rawApi<ZoneWithSpots[]>('GET', FLOOR_PATH),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });

  const savePositions = useMutation({
    mutationFn: (positions: { id: string; x?: number; y?: number; width?: number; height?: number }[]) =>
      rawApi('PATCH', '/admin/v1/floor/spots/positions', positions),
    onSuccess: (_d, positions) => {
      setMoved({});
      refresh();
      notifyDone(
        'Layout saved',
        `${positions.length} ${positions.length === 1 ? 'change' : 'changes'} to the plan.`,
      );
    },
    onError: (err) => notifyError(err, 'save the layout'),
  });

  /**
   * A quarter turn on the spot: across and deep trade places, pivoting about the middle so the
   * thing stays where it stands instead of swinging around its own corner. Staged exactly like a
   * move, so Save layout and Undo moves cover it with everything else.
   *
   * Half of what the owner's "mirror the orientation of the bartop" asked for: a silhouette
   * follows its own proportions, so swapping them redraws the counter running the other way. The
   * other half — the same footprint FACING the other way — is `flipped`, the half-turn the Mirror
   * button below toggles. Between the two, every facing the plan can draw is reachable by hand.
   */
  const turn = (spot: Spot) => {
    const x = Math.max(0, Math.round(spot.x + spot.width / 2 - spot.height / 2));
    const y = Math.max(0, Math.round(spot.y + spot.height / 2 - spot.width / 2));
    setMoved((m) => ({
      ...m,
      [spot.id]: { ...m[spot.id], x, y, width: spot.height, height: spot.width },
    }));
  };

  /**
   * Facing is a fact about the thing, not about the arrangement: the sign's arrow points at the
   * beach or it does not, whichever way the sign is later dragged. So Mirror saves through the
   * same per-spot PATCH the properties dialog rides, immediately, instead of staging with the
   * moves — a facing held hostage by "Save layout" would leave the arrow pointing the wrong way
   * until some unrelated rearranging was committed. The drawing turning around on the spot is its
   * own confirmation, so there is no toast.
   */
  const mirror = useMutation({
    mutationFn: (spot: Spot) =>
      rawApi('PATCH', `/admin/v1/floor/spots/${spot.id}`, { flipped: !spot.flipped }),
    onSuccess: refresh,
    onError: (err) => notifyError(err, 'mirror it'),
  });

  const allSpots = (zones.data ?? []).flatMap((z) => z.spots ?? []);
  const onPlan = new Set(allSpots.map((s) => s.id));
  /**
   * The arrangement waiting to be saved, minus anything that is no longer there to arrange.
   *
   * Removing is now something you do to what you have just been moving: pick up a row of chairs,
   * shuffle them along, decide two of them have to go. The server saves an arrangement all or
   * nothing, so one dragged-then-removed chair made "Save layout" fail outright — and go on
   * failing, because the reference to the gone chair stayed. Every other move sat there unsaved
   * with no way out except "Undo moves", which throws the whole afternoon away.
   */
  const staged = Object.entries(moved).filter(([id]) => onPlan.has(id));
  const dirty = staged.length > 0;

  /**
   * Taking things off the plan that the server would not delete.
   *
   * One at a time on purpose: there is no batch route, and inventing one would put a shape into an
   * API a register has to speak for years to save a handful of round trips on a screen the owner opens
   * once a season.
   */
  const takeOffPlan = useMutation({
    mutationFn: async (targets: Spot[]) => {
      const off: string[] = [];
      let failure: unknown = null;
      for (const spot of targets) {
        try {
          await rawApi('PATCH', `/admin/v1/floor/spots/${spot.id}`, { active: false });
          off.push(spotName(spot));
        } catch (err) {
          failure = err;
          break;
        }
      }
      return { off, failure };
    },
    onSuccess: ({ off, failure }) => {
      refresh();
      if (off.length > 0) {
        notifyDone(
          'Taken off the plan',
          `${nameList(off)} ${off.length === 1 ? 'is' : 'are'} no longer somewhere anyone can be seated. Past orders keep ${off.length === 1 ? 'it' : 'them'}.`,
        );
      }
      if (failure) notifyError(failure, 'take those off the plan');
    },
  });

  /**
   * Removing what is picked, for good.
   *
   * The server is what decides, not this screen: it refuses with `spot_has_orders` when deleting
   * would take a night's tabs with it. A mixed handful — four palms and the table the party sat at —
   * would otherwise half-vanish with nothing said, so every refusal is collected and the whole
   * outcome is reported in one go, with taking the refused ones off the plan offered straight after.
   */
  const removeSelected = useMutation({
    mutationFn: async (targets: Spot[]) => {
      const gone: string[] = [];
      const seated: Spot[] = [];
      let failure: unknown = null;
      for (const spot of targets) {
        try {
          await rawApi('DELETE', `/admin/v1/floor/spots/${spot.id}`);
          gone.push(spotName(spot));
        } catch (err) {
          if (err instanceof ApiError && err.code === 'spot_has_orders') {
            seated.push(spot);
            continue;
          }
          failure = err;
          break;
        }
      }
      return { gone, seated, failure };
    },
    onSuccess: ({ gone, seated, failure }) => {
      refresh();
      // Whatever survived stays picked, so the plan still points at what the message is about.
      setSelectedIds(seated.map((s) => s.id));
      if (gone.length > 0) {
        notifyDone('Removed', `${nameList(gone)} ${gone.length === 1 ? 'is' : 'are'} gone from the plan.`);
      }
      if (failure) {
        notifyError(failure, 'remove everything you picked');
        return;
      }
      if (seated.length === 0) return;
      modals.openConfirmModal({
        title:
          seated.length === 1 ? 'Guests have been seated here' : 'Guests have been seated at some of these',
        children: (
          <Text size="sm">
            {nameList(seated.map(spotName))} {seated.length === 1 ? 'has' : 'have'} had orders against{' '}
            {seated.length === 1 ? 'it' : 'them'}, so deleting would take that history too.{' '}
            {seated.length === 1 ? 'It' : 'They'} can be taken off the plan instead — nobody can be seated
            there any more, and the past stays intact.
          </Text>
        ),
        labels: {
          confirm: seated.length === 1 ? 'Take it off the plan' : 'Take them off the plan',
          cancel: seated.length === 1 ? 'Leave it alone' : 'Leave them alone',
        },
        onConfirm: () => takeOffPlan.mutate(seated),
      });
    },
    onError: (err) => notifyError(err, 'remove everything you picked'),
  });

  const busyRemoving = removeSelected.isPending || takeOffPlan.isPending;

  /** One question before anything goes, so a stray Delete key is not the end of a palm tree. */
  const askRemove = (targets: Spot[]) => {
    if (targets.length === 0 || busyRemoving) return;
    modals.openConfirmModal({
      title: targets.length === 1 ? `Remove ${spotName(targets[0]!)}?` : `Remove these ${targets.length}?`,
      children: (
        <Text size="sm">
          {targets.length === 1
            ? 'It comes off the plan for good.'
            : `${nameList(targets.map(spotName))} come off the plan for good. Anything guests have been seated at is kept back, and you will be asked what to do with it.`}
        </Text>
      ),
      labels: {
        confirm: targets.length === 1 ? 'Remove it' : 'Remove them',
        cancel: targets.length === 1 ? 'Keep it' : 'Keep them',
      },
      confirmProps: { color: 'red' },
      onConfirm: () => removeSelected.mutate(targets),
    });
  };

  /**
   * The keys anybody already expects to remove things, and the one that means "never mind".
   *
   * Removing was reachable only through a right-click menu, which on a Mac trackpad is a gesture
   * most people never make — so the owner's honest report was that nothing could be deleted at all.
   *
   * Ignored while a dialog is open or while the cursor is in a box: Backspace in the name field
   * means rub out a letter, and having it delete the table instead is unforgivable.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector('[role="dialog"]')) return;
      const focused = document.activeElement as HTMLElement | null;
      if (focused && (focused.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(focused.tagName))) {
        return;
      }
      if (e.key === 'Escape') {
        setSelectedIds([]);
        return;
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const targets = allSpots.filter((s) => selectedIds.includes(s.id));
      if (targets.length === 0) return;
      // Backspace still walks some browsers back a page, which would lose the unsaved arrangement.
      e.preventDefault();
      askRemove(targets);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // No dependency list on purpose: the handler has to see the selection as it is at the moment
    // the key is pressed, and every value it reads is rebuilt on each render anyway.
  });

  // "Can we take a party of thirty on Saturday?" is asked of the whole property, not of one area,
  // and answering it meant adding up the line under each heading by hand. Closed areas are left
  // out: a total that counts the seats on a deck nobody can be sat on is worse than no total.
  const openAreas = (zones.data ?? []).filter((z) => z.zone.active !== false);
  const houseSeats = openAreas
    .flatMap((z) => z.spots ?? [])
    .filter((s) => s.active && s.assignable !== false)
    .reduce((sum, s) => sum + (s.seats ?? 0), 0);

  const { order: areaOrder, reorder: reorderAreas } = useAreaOrder(zones.data);
  // The panels follow the order list the moment it is dragged, rather than after the save comes
  // back — a list that says one thing while the areas below it say another is not worth trusting.
  const orderedZones = areaOrder
    .map((id) => (zones.data ?? []).find((z) => z.zone.id === id))
    .filter((z): z is ZoneWithSpots => Boolean(z));
  // A new area goes to the bottom, where the owner can see it arrive. Landing it on 0 would put it
  // above the beach every time, which is the one place nobody ever means to put a new deck.
  const nextAreaOrder = Math.max(0, ...(zones.data ?? []).map((z) => z.zone.displayOrder ?? 0)) + 1;

  return (
    <>
      <Group justify="space-between" mb="md" align="flex-start">
        <div>
          <Title order={3}>Floor plan</Title>
          {houseSeats > 0 && (
            <Text size="sm">
              Seats {houseSeats} across {openAreas.length} {openAreas.length === 1 ? 'area' : 'areas'} in use
            </Text>
          )}
          <Text size="sm" c="dimmed">
            {placing
              ? `Click the plan to put down a ${kindLabel(placing)}${isAssignableKind(placing) ? '' : ' — keep clicking for more'}.`
              : 'Pick something below, then click the plan to place it. Click something to pick it — shift-click or drag a box round several. Drag to rearrange, right-click to change it.'}
          </Text>
        </div>
        <Group gap="xs">
          {dirty && (
            <>
              <Button variant="default" onClick={() => setMoved({})} disabled={savePositions.isPending}>
                Undo moves
              </Button>
              <Button
                loading={savePositions.isPending}
                onClick={() => savePositions.mutate(staged.map(([id, pos]) => ({ id, ...pos })))}
              >
                Save layout
              </Button>
            </>
          )}
          {!dirty && (
            <Button variant="light" onClick={() => setAddingArea(true)}>
              Add an area
            </Button>
          )}
        </Group>
      </Group>

      <Palette placing={placing} onPick={setPlacing} />

      {zones.isError && (
        <Alert color="red" title="Could not load the floor plan" mb="md">
          The layout could not be reached. Nothing has been lost — this is only a display problem.
        </Alert>
      )}

      {!zones.isLoading && (zones.data ?? []).length === 0 && (
        <Alert color="blue" variant="light" title="No areas yet">
          An area is a part of the property — the beach, the inside bar. Add one, then double-click its floor
          to put tables on it.
        </Alert>
      )}

      {orderedZones.length > 1 && (
        <AreaOrder areas={orderedZones.map((z) => z.zone)} order={areaOrder} onReorder={reorderAreas} />
      )}

      <Stack gap="lg">
        {orderedZones.map(({ zone, spots }) => {
          const positioned = (spots ?? []).map((s) => (moved[s.id] ? { ...s, ...moved[s.id] } : s));
          // Scenery is drawn but never counted — the dock is not a place, and a summary that says
          // "22 places" when four of them are palm trees is worse than no summary.
          const active = positioned.filter((s) => s.active && s.assignable !== false);
          const scenery = positioned.filter((s) => s.active && s.assignable === false);
          // Places taken off the plan are still drawn, so that a mis-click can be undone. Saying
          // how many there are is the difference between a plan that has one dead table on it and
          // a plan a manager quietly stops trusting.
          const offPlan = positioned.filter((s) => !s.active);
          // One selection is shared by every area's plan, and the map keeps a selection inside the
          // area it was made in — so at most one of these bars is ever on screen.
          const picked = positioned.filter((s) => selectedIds.includes(s.id));
          const seatTotal = active.reduce((sum, s) => sum + (s.seats ?? 0), 0);
          const joinable = active.filter((s) => s.combinable).reduce((sum, s) => sum + (s.seats ?? 0), 0);
          return (
            <Paper key={zone.id} withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs" align="flex-start" wrap="nowrap">
                <div>
                  <Group gap="xs">
                    <Title order={5}>{zone.name}</Title>
                    {zone.active === false && (
                      <Badge color="gray" variant="light">
                        Not in use
                      </Badge>
                    )}
                  </Group>
                  {/* "16 of those can be joined up" sat next to "6 places" and read as sixteen
                      tables. It is a seat count, so it has to say seats. And "3 marked up" meant
                      the walls and palms — in a bar, "marked up" is what you do to a price. */}
                  <Text size="xs" c="dimmed">
                    {active.length} {active.length === 1 ? 'place' : 'places'} · seats {seatTotal}
                    {joinable > 0 && ` · ${joinable} of those seats can be joined up for a larger group`}
                    {scenery.length > 0 &&
                      ` · ${scenery.length} ${scenery.length === 1 ? 'wall or fixture' : 'walls and fixtures'}`}
                    {offPlan.length > 0 && ` · ${offPlan.length} off the plan`}
                  </Text>
                </div>
                {/* Not "Rename": this dialog is now also the only way back for an area taken out
                    of use, and a button naming one of the two things it does is how a closed area
                    stays closed — nobody opens "Rename" looking to reopen the upstairs deck. */}
                <Button size="compact-sm" variant="subtle" onClick={() => setEditingZone(zone)}>
                  Edit area
                </Button>
              </Group>

              {/* The size, said in numbers, beside the plan it describes. Typing a size used to mean
                  guessing: nothing anywhere said how far the furniture already reached, so the only
                  way to find a number that fitted was to try one and look. */}
              <Group gap="xs" align="center" mb="xs" wrap="wrap">
                {(() => {
                  const staged = roomSize[zone.id];
                  const w = staged?.width ?? zone.width ?? null;
                  const h = staged?.height ?? zone.height ?? null;
                  const reach = extents[zone.id];
                  const outside = reach?.outside ?? 0;
                  return (
                    <>
                      <Text size="xs" c="dimmed">
                        {w && h
                          ? `Area is ${w} across by ${h} deep — drag the corner marker to change it`
                          : 'This area has no size of its own, so it grows to fit what you put in it'}
                      </Text>
                      {reach && reach.contentW > 0 && (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() =>
                            saveRoom.mutate({
                              id: zone.id,
                              // A margin past the furthest thing, so the far corner is reachable
                              // rather than flush against the last table.
                              width: Math.ceil((reach.contentW + 40) / 20) * 20,
                              height: Math.ceil((reach.contentH + 40) / 20) * 20,
                            })
                          }
                          loading={saveRoom.isPending}
                        >
                          Fit to what is here
                        </Button>
                      )}
                      {staged && (
                        <Button
                          size="compact-xs"
                          onClick={() => saveRoom.mutate({ id: zone.id, ...staged })}
                          loading={saveRoom.isPending}
                        >
                          Save size
                        </Button>
                      )}
                      {outside > 0 && (
                        <Text size="xs" c="orange.8">
                          {outside === 1 ? '1 thing sits' : `${outside} things sit`} outside the area — drag{' '}
                          {outside === 1 ? 'it' : 'them'} in, or make the area bigger
                        </Text>
                      )}
                    </>
                  );
                })()}
              </Group>
              <ScrollArea>
                <FloorMap
                  spots={positioned}
                  selectedIds={selectedIds}
                  onSelect={setSelectedIds}
                  room={roomSize[zone.id] ?? zone}
                  onRoomResize={(width, height) =>
                    setRoomSize((r) => ({ ...r, [zone.id]: { width, height } }))
                  }
                  onExtentChange={(info) =>
                    setExtents((e) =>
                      e[zone.id] &&
                      e[zone.id]?.contentW === info.contentW &&
                      e[zone.id]?.contentH === info.contentH &&
                      e[zone.id]?.outside === info.outside
                        ? e
                        : { ...e, [zone.id]: info },
                    )
                  }
                  onMove={(id, x, y) => setMoved((m) => ({ ...m, [id]: { ...m[id], x, y } }))}
                  onResize={(id, width, height) =>
                    setMoved((m) => ({ ...m, [id]: { ...m[id], width, height } }))
                  }
                  placing={Boolean(placing)}
                  onAddAt={(x, y) => {
                    const at = { zoneId: zone.id, kind: placing ?? undefined, x, y };
                    // Stays armed for scenery: a wall is six segments and a beach is five palms,
                    // and re-picking the same button between each one is the friction this removes.
                    if (placing && !isAssignableKind(placing)) {
                      dropDecor.mutate(at);
                      return;
                    }
                    setAdding(at);
                    setPlacing(null);
                  }}
                  onOpen={(id) => setEditing(allSpots.find((s) => s.id === id) ?? null)}
                />
              </ScrollArea>
              {/* Removing anything used to mean a menu that opens on right-click, which on a
                  trackpad is a gesture most people never make — so as far as the owner could tell,
                  nothing on his floor plan could be deleted at all. This is the second way in.

                  Below the plan and not above it on purpose: a row appearing above would push the
                  whole drawing down the instant something is picked, and the shape you just took
                  hold of would slide out from under your finger as you started to drag it. */}
              {picked.length > 0 ? (
                <Group gap="xs" mt={6} wrap="wrap">
                  <Text size="sm" fw={500}>
                    {picked.length === 1 ? spotName(picked[0]!) : `${picked.length} things`} picked
                  </Text>
                  {picked.length === 1 && (
                    <Button
                      size="compact-xs"
                      variant="default"
                      onClick={() => setEditing(allSpots.find((s) => s.id === picked[0]!.id) ?? null)}
                    >
                      Change it
                    </Button>
                  )}
                  {/* Only for things with a longer way to face: turning a square is indistinguishable
                      from doing nothing, and a button that visibly does nothing teaches people to
                      stop trusting the ones that do. */}
                  {picked.length === 1 && picked[0]!.width !== picked[0]!.height && (
                    <Button
                      size="compact-xs"
                      variant="default"
                      title="A quarter turn — across and deep trade places"
                      onClick={() => turn(picked[0]!)}
                    >
                      Turn it
                    </Button>
                  )}
                  {/* Only for the silhouettes a half-turn visibly changes — the sign, the bar
                      counter, the lounger. Everything else draws the same both ways round, and a
                      button that visibly does nothing is worse than no button; see mirrorable. */}
                  {picked.length === 1 &&
                    mirrorable(picked[0]!.kind, picked[0]!.shape, picked[0]!.assignable !== false) && (
                      <Button
                        size="compact-xs"
                        variant="default"
                        title="The same footprint, facing the other way"
                        loading={mirror.isPending}
                        onClick={() => mirror.mutate(picked[0]!)}
                      >
                        Mirror it
                      </Button>
                    )}
                  <Button
                    size="compact-xs"
                    color="red"
                    variant="light"
                    loading={busyRemoving}
                    onClick={() => askRemove(picked)}
                  >
                    {picked.length === 1 ? 'Remove it' : `Remove these ${picked.length}`}
                  </Button>
                  <Button size="compact-xs" variant="subtle" onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                  <Text size="xs" c="dimmed">
                    The Delete key does the same.
                  </Text>
                </Group>
              ) : (
                <Text size="xs" c="dimmed" mt={6}>
                  Solid edge: fixed in place. Dashed edge: can be moved.
                </Text>
              )}
            </Paper>
          );
        })}
      </Stack>

      <SpotPropertiesModal
        spot={editing}
        createAt={adding}
        onClose={() => {
          setEditing(null);
          setAdding(null);
        }}
      />

      <AreaModal
        zone={editingZone}
        creating={addingArea}
        locationId={locationId}
        nextOrder={nextAreaOrder}
        onClose={() => {
          setEditingZone(null);
          setAddingArea(false);
        }}
      />
    </>
  );
}

/**
 * Areas are two fields, so they get a dialog rather than a table of their own. The Spanish name is
 * here because the register and the kitchen screens are bilingual — it is never shown in this admin,
 * which is English only, so the field says plainly what it is for.
 */
function AreaModal({
  zone,
  creating,
  locationId,
  nextOrder,
  onClose,
}: {
  zone: Zone | null;
  creating: boolean;
  locationId: string | undefined;
  /** Where a brand new area lands: the bottom of the list, until it is dragged somewhere else. */
  nextOrder: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [width, setWidth] = useState<number | ''>('');
  const [height, setHeight] = useState<number | ''>('');
  const opened = Boolean(zone) || creating;

  // Re-seed when the target changes rather than leaving a stale draft on screen.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const key = zone?.id ?? (creating ? 'new' : null);
  if (key !== seededFor) {
    setSeededFor(key);
    setName(zone?.name ?? '');
    setNameEs(zone?.nameEs ?? '');
    setWidth(zone?.width ?? '');
    setHeight(zone?.height ?? '');
  }

  // Blank means "no shape of its own", which is a real answer and not a missing one: the plan then
  // grows to fit whatever is on it, exactly as every area did before areas could be sized.
  // Clamped here rather than on every keystroke: typing "120" into a box with a minimum of 80 turned
  // the "1" into "80" before the "2" arrived, so those sizes could not be typed at all.
  const atLeast = (v: number | '', min: number) => (v === '' ? null : Math.max(min, Number(v) || min));
  const sized = { width: atLeast(width, 80), height: atLeast(height, 80) };

  const save = useMutation({
    mutationFn: () =>
      zone
        ? // Where the area comes in the list is not sent from here at all any more: it is dragged
          // on the floor plan page and saved as one whole order. Sending it alongside a rename
          // would put a second author on the same number, which is how two areas ended up sharing
          // a position in the first place.
          rawApi('PATCH', `/admin/v1/floor/zones/${zone.id}`, {
            name,
            nameEs: nameEs || null,
            ...sized,
          })
        : rawApi('POST', FLOOR_PATH, {
            locationId,
            name,
            nameEs: nameEs || null,
            displayOrder: nextOrder,
            ...sized,
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });
      // Not "Renamed": this dialog also sets the Spanish name the register shows, and a confirmation
      // naming the wrong change is how you stop reading confirmations.
      notifyDone(zone ? 'Area saved' : 'Area added', `${name} is on the floor plan.`);
      onClose();
    },
    onError: (err) => notifyError(err, zone ? 'rename this area' : 'add this area'),
  });

  /**
   * An area added by mistake, or one that is shut for the season, was permanent: areas could be
   * created and renamed and never removed, so a typo made in a hurry became a heading on the floor
   * plan forever. Taking it out of use rather than deleting it keeps every past order that names a
   * table inside it.
   */
  const offPlan = zone?.active === false;
  const setInUse = useMutation({
    mutationFn: (inUse: boolean) => rawApi('PATCH', `/admin/v1/floor/zones/${zone?.id}`, { active: inUse }),
    onSuccess: (_result, inUse) => {
      void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });
      notifyDone(
        inUse ? 'Back in use' : 'Taken out of use',
        inUse
          ? `${zone?.name} is open again.`
          : `${zone?.name} and everything in it is closed. Nobody can be seated there.`,
      );
      onClose();
    },
    onError: (err, inUse) => notifyError(err, inUse ? 'reopen this area' : 'close this area'),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={zone ? zone.name : 'Add an area'}>
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="Beach"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          data-autofocus
          required
        />
        {/* The room's own outline. Without it the plan has no edges — it grew and shrank as
            furniture moved, so there was no far corner to put a table in until something was
            already standing there. Arbitrary units on purpose: nobody is going to measure the beach. */}
        <div>
          <Text size="sm" fw={500} mb={4}>
            Shape of the area
          </Text>
          <Group grow>
            <NumberInput
              label="Across"
              placeholder="Grows to fit"
              value={width}
              onChange={(v) => setWidth(v === '' ? '' : Number(v))}
              min={80}
              step={20}
              clampBehavior="blur"
            />
            <NumberInput
              label="Deep"
              placeholder="Grows to fit"
              value={height}
              onChange={(v) => setHeight(v === '' ? '' : Number(v))}
              min={80}
              step={20}
              clampBehavior="blur"
            />
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            Leave both empty and the area sizes itself to whatever you put in it.
          </Text>
        </div>
        <TextInput
          label="Name in Spanish"
          description="Used on the register and kitchen screens for staff working in Spanish. It never appears here."
          placeholder="Playa"
          value={nameEs}
          onChange={(e) => setNameEs(e.currentTarget.value)}
        />
        {/* "Where it comes in the list" was a number typed in here, and the owner named what it
            cost him: two areas could be given the same number, and the numbers could run 0, 4, 40
            with nothing in between. It is dragged on the floor plan page now, so there is no number
            to get wrong. */}
        {!zone && (
          <Text size="xs" c="dimmed">
            It goes to the bottom of the list. Drag it where you want it afterwards.
          </Text>
        )}
        <Group justify="space-between">
          {zone ? (
            <Button
              variant="subtle"
              color={offPlan ? 'teal' : 'red'}
              onClick={() => setInUse.mutate(offPlan)}
              loading={setInUse.isPending}
            >
              {offPlan ? 'Put back in use' : 'Take out of use'}
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={save.isPending || setInUse.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!name.trim() || (!zone && !locationId)}
            >
              {zone ? 'Save' : 'Add area'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
